import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, Calendar } from 'lucide-react';
import { useCart } from '../context/CartContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { getItemCount } = useCart();
  const itemCount = getItemCount();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Menu', path: '/menu' },
    { name: 'Reservations', path: '/booking' },
    { name: 'Availability', path: '/availability' },
    { name: 'Order Online', path: '/order' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        isScrolled 
          ? 'glass-effect' 
          : 'bg-white/80 backdrop-blur-sm'
      }`}
      data-testid="navbar"
    >
      <div className="container-elegant">
        <div className="flex items-center justify-between h-24 md:h-28">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center"
            data-testid="logo-link"
          >
            <img 
              src="/reva-logo-transparent.png" 
              alt="Reva Dining" 
              className="h-24 md:h-28 w-auto object-contain"
              style={{
                filter: 'contrast(1.2) saturate(1.1)'
              }}
            />
            {/* Backup - Old Logo URL: https://customer-assets.emergentagent.com/job_silver-spoon-bistro/artifacts/f0sls4ki_REVA%20.png */}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-sans tracking-widest uppercase transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-malva-dark'
                    : 'text-gray-600 hover:text-malva-dark'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Cart & Account & Mobile Menu */}
          <div className="flex items-center gap-4">
            <Link 
              to="/account" 
              className="relative p-2 text-gray-600 hover:text-malva-dark transition-colors duration-300"
              data-testid="account-link"
              title="My Account"
            >
              <User className="w-6 h-6" />
            </Link>
            <Link 
              to="/cart" 
              className="relative p-2 text-gray-600 hover:text-malva-dark transition-colors duration-300"
              data-testid="cart-link"
            >
              <ShoppingBag className="w-6 h-6" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-malva text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-sans">
                  {itemCount}
                </span>
              )}
            </Link>
            
            <button
              className="lg:hidden p-2 text-gray-600 hover:text-malva-dark transition-colors duration-300"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`lg:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg transition-all duration-500 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
        data-testid="mobile-menu"
      >
        <div className="container-elegant py-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block text-sm font-sans tracking-widest uppercase py-2 transition-colors duration-300 ${
                location.pathname === link.path
                  ? 'text-malva-dark'
                  : 'text-gray-600 hover:text-malva-dark'
              }`}
              data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
            >
              {link.name}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
